import React, { useState, useEffect } from 'react';
import { Bell, X, Check, Trash2, BookOpen, Trophy, Sparkles } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../utils/auth';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';

export const NotificationsPanel = ({ isOpen, onClose }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchNotifications = async () => {
        try {
            const user = await getCurrentUser();
            if (!user) return;

            const { data, error } = await supabase
                .from('notifications')
                .select('*')
                .eq('user_id', user.id)
                .order('sent_at', { ascending: false })
                .limit(20);

            if (error) throw error;
            setNotifications(data || []);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    const markAsRead = async (id) => {
        try {
            const { error } = await supabase
                .from('notifications')
                .update({ read: true, read_at: new Date().toISOString() })
                .eq('id', id);

            if (error) throw error;
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const deleteNotification = async (id) => {
        try {
            await axios.delete(`/api/notifications/${id}`);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'welcome': return <Sparkles className="w-5 h-5 text-primary" />;
            case 'motivational': return <Bell className="w-5 h-5 text-orange-500" />;
            case 'achievement': return <Trophy className="w-5 h-5 text-yellow-500" />;
            case 'progress': return <BookOpen className="w-5 h-5 text-blue-500" />;
            case 'daily_verse': return <BookOpen className="w-5 h-5 text-purple-600" />;
            default: return <Bell className="w-5 h-5 text-gray-500" />;
        }
    };

    const getIconBgColor = (type, read) => {
        if (read) return 'bg-secondary';
        switch (type) {
            case 'welcome': return 'bg-primary/20';
            case 'motivational': return 'bg-orange-100';
            case 'achievement': return 'bg-yellow-100';
            case 'progress': return 'bg-blue-100';
            case 'daily_verse': return 'bg-purple-100';
            default: return 'bg-gray-100';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300">
            <div
                className="animate-in fade-in zoom-in duration-200 w-full max-w-lg bg-card rounded-[32px] shadow-2xl overflow-hidden border border-white/20"
            >
                <div className="p-6 border-b border-border flex items-center justify-between bg-gradient-to-r from-primary/10 to-transparent">
                    <div className="flex items-center gap-3">
                        <img
                            src="/logo.png"
                            alt="Logo"
                            className="w-10 h-10 rounded-xl shadow-md"
                        />
                        <div>
                            <h2 className="text-xl font-black">Notificaciones</h2>
                            <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Últimas noticias</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-secondary rounded-full transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto p-4 space-y-3 custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
                            <p className="text-sm text-muted-foreground font-medium italic">Cargando mensajes...</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="text-center py-16 px-6">
                            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 grayscale opacity-50">
                                <Bell className="w-10 h-10 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-bold mb-1">Sin notificaciones</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                Aquí recibirás noticias sobre tu progreso, nuevos logros y actualizaciones.
                            </p>
                        </div>
                    ) : (
                        notifications.map((notif) => (
                            <div
                                key={notif.id}
                                className={`group relative p-4 rounded-2xl border-2 transition-all duration-200 ${notif.read
                                    ? 'bg-card border-border/50 opacity-80'
                                    : 'bg-gradient-to-br from-primary/5 to-card border-primary/20 shadow-md ring-1 ring-primary/10'
                                    }`}
                            >
                                {!notif.read && (
                                    <div className="absolute top-4 right-4 w-2.5 h-2.5 bg-primary rounded-full animate-pulse shadow-glow"></div>
                                )}

                                <div className="flex gap-4">
                                    <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${getIconBgColor(notif.type, notif.read)}`}>
                                        {getIcon(notif.type)}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-bold text-sm mb-1 line-clamp-1">{notif.title}</h4>
                                        <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line line-clamp-6 mb-3">
                                            {notif.message}
                                        </p>

                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                                                {format(new Date(notif.sent_at), "d MMM, HH:mm", { locale: es })}
                                            </span>

                                            <div className="flex items-center gap-2">
                                                {!notif.read && (
                                                    <button
                                                        onClick={() => markAsRead(notif.id)}
                                                        className="p-1.5 hover:bg-primary/10 rounded-lg text-primary transition-colors tooltip"
                                                        title="Marcar como leída"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => deleteNotification(notif.id)}
                                                    className="p-1.5 hover:bg-destructive/10 rounded-lg text-destructive opacity-0 group-hover:opacity-100 transition-all"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {notifications.length > 0 && (
                    <div className="p-4 bg-secondary/30 border-t border-border mt-auto">
                        <p className="text-[10px] text-center text-muted-foreground font-bold uppercase tracking-widest">
                            Mostrando las últimas 20 notificaciones
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
